import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { useAppSelector } from '@/store/hooks';
import type { MtrBulkScope } from '@/hooks/useCollectsApiData';
import type { FilterState } from '@/hooks/useFilters';
import {
  listFilterForScope,
  resolveMtrBulkFilter,
  type MtrBulkFilterParams,
  type ResolvedMtrBulkFilter,
} from '@/services/mtrBulkFilter';

const STALE = 2 * 60 * 1000;

/**
 * Filtro do lote de MTR/CDF resolvido (ids pré-recortados no agreements), partilhado entre o
 * resumo e o download pela mesma chave de cache: o modal resolve uma vez e os lotes reusam.
 */
export function useMtrBulkFilterParams(filters: FilterState, scope: MtrBulkScope) {
  const { profile } = useUnifiedAuth();
  const generators = useAppSelector((s) => s.establishmentAccess.generators);
  const establishments = useAppSelector((s) => s.establishmentAccess.establishments);

  const params = useMemo<MtrBulkFilterParams>(
    () => ({ filters, profile, generators, establishments, scope }),
    [filters, profile, generators, establishments, scope],
  );
  const queryKey = useMemo(() => ['mtr-bulk-filter', scope, listFilterForScope(params)] as const, [params, scope]);

  return { params, queryKey, establishments };
}

export function useMtrBulkFilter(filters: FilterState, scope: MtrBulkScope, enabled: boolean) {
  const { params, queryKey } = useMtrBulkFilterParams(filters, scope);
  return useQuery<ResolvedMtrBulkFilter>({
    queryKey,
    queryFn: () => resolveMtrBulkFilter(params),
    enabled,
    staleTime: STALE,
  });
}

/** Mesmo filtro, para quem precisa dele num evento (download de lote): usa o cache se já resolvido. */
export function useEnsureMtrBulkFilter(filters: FilterState, scope: MtrBulkScope) {
  const { params, queryKey } = useMtrBulkFilterParams(filters, scope);
  const queryClient = useQueryClient();
  return () =>
    queryClient.ensureQueryData<ResolvedMtrBulkFilter>({
      queryKey,
      queryFn: () => resolveMtrBulkFilter(params),
      staleTime: STALE,
    });
}
